import { Grid } from '@mui/material';
import { useContext, useState } from 'react';
import 'moment/locale/en-gb';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';
import { GroupContext } from '../../../context/GroupContext';
import { CompanyContext } from '../../../context/CompanyContext';
import { UserContext } from '../../../context/UserContext';
import ExportDataButton, {
  ExportRequestParameters,
  ExportParametersProps
} from '../ExportDataButton';
import { momentISOString } from '../../../util/date';

// Data sent in request to unique Cloud Function.
interface UserActivityExportRequestParameters extends ExportRequestParameters {
  group_id: string;
  company_id: string;
  start: string;
  end: string;
  super_admin_export: boolean;
}

/**
 * Reusable importing context for metric record file data containing relevant inputs for the microservice.
 * @param {void} handleContextChange Update context in parent from input changes.
 * @param {void} handleContextValid Update context validity in parent based on input changes.
 * @returns {JSX.Element}
 */
const UserActivityExportParameters = ({ export_type }: ExportParametersProps) => {
  const { user } = useContext(UserContext);
  const { group } = useContext(GroupContext);
  const { company } = useContext(CompanyContext);

  if (!user || !group || !company) {
    return null;
  }

  // Default parameters state for inputs.
  const initial_parameters: UserActivityExportRequestParameters = {
    group_id: group.id,
    company_id: company.id,
    start: '',
    end: '',
    super_admin_export: user.super_admin
  };

  const [userActivityExportParameters, setUserActivityExportParameters] =
    useState<UserActivityExportRequestParameters>(initial_parameters);
  const [startDate, setStartDate] = useState<Moment | null>(null);
  const [endDate, setEndDate] = useState<Moment | null>(null);

  // Validation check for unique export parameters.
  const parameters_valid = Boolean(
    startDate &&
    endDate &&
    startDate > moment().subtract(2, 'years') &&
    endDate >= startDate &&
    endDate < moment().add(1, 'day')
  );

  const handleStartDateChange = (start_date: Moment | null): void => {
    const updated_parameters: UserActivityExportRequestParameters = {
      ...userActivityExportParameters,
      start: start_date ? momentISOString(start_date) : ''
    };
    setStartDate(start_date);
    setUserActivityExportParameters(updated_parameters);
  };

  const handleEndDateChange = (end_date: Moment | null): void => {
    // Add 24 hours to end date to retrieve full final day data.
    const end_date_inclusive = end_date ? moment(end_date).add(1, 'day') : null;

    const updated_parameters: UserActivityExportRequestParameters = {
      ...userActivityExportParameters,
      end: end_date_inclusive ? momentISOString(end_date_inclusive) : ''
    };

    setEndDate(end_date);
    setUserActivityExportParameters(updated_parameters);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="en-gb">
      <Grid container spacing={2}>
        <Grid size={'auto'} sx={{ minWidth: 250 }}>
          <DatePicker<Moment>
            label={'Start Date'}
            value={startDate}
            disableFuture
            maxDate={endDate || undefined}
            minDate={moment().subtract(2, 'years')}
            onChange={(new_date) => handleStartDateChange(new_date)}
            sx={{ width: '100%' }}
          />
        </Grid>
        <Grid size={'auto'} sx={{ minWidth: 250 }}>
          <DatePicker<Moment>
            label={'End Date'}
            value={endDate}
            disableFuture
            minDate={startDate ? startDate : moment().subtract(2, 'years')}
            onChange={(new_date) => handleEndDateChange(new_date)}
            sx={{ width: '100%' }}
          />
        </Grid>
        <Grid size={'auto'} sx={{ minWidth: 250 }}>
          <ExportDataButton
            user={user}
            export_type={export_type}
            parameters={userActivityExportParameters}
            disabled={!parameters_valid}
            label="Export"
          />
        </Grid>
      </Grid>
    </LocalizationProvider>
  );
};

export default UserActivityExportParameters;
